import { useQuery } from '@tanstack/react-query';

import { AuthApi, AuthQueryOptions, UnauthApi } from '@/api';
import { GEOLOCATION_VIEW_TYPE } from '@/constants/geo';
import { useAuthStore } from '@/stores/AuthStore';

type GeocodingParams = NonNullable<
  Parameters<
    (typeof AuthApi)['get:/wello/v2/auth/wello-policy-code/reverse-geocoding']
  >[number]
>['query'];
type GeolocationParams = NonNullable<
  Parameters<
    (typeof AuthApi)['get:/wello/v2/auth/wello-policy-code/geolocation']
  >[number]
>['query'];

/**
 * 로그인 유저면서 위치 이용 동의를 했는지 여부에 따라 사용자의 지리 정보를 서버에 저장
 * auth api를 사용했을 경우에만 서버에 저장됨
 */
export const useGeoApis = () => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const { data: isLocationServiceConsent } = useQuery({
    ...AuthQueryOptions[
      'get:/wello/v2/auth/member-wello/location-service-consent'
    ](),
    enabled: !!isLogin,
    select: (data) =>
      isLogin ? (data.context?.locationServiceConsentYN ?? false) : true,
  });

  const getGeocoding = (params: {
    coords: GeocodingParams['coords'];
    geolocationViewType?: GEOLOCATION_VIEW_TYPE;
  }) => {
    if (isLogin && isLocationServiceConsent) {
      return AuthApi['get:/wello/v2/auth/wello-policy-code/reverse-geocoding']({
        query: {
          coords: params.coords,
          geolocationViewType:
            params.geolocationViewType ?? GEOLOCATION_VIEW_TYPE.WELLO_CLIENT,
        },
      });
    }

    return UnauthApi[
      'get:/wello/v2/allows/wello-policy-code/reverse-geocoding'
    ]({
      query: {
        coords: params.coords,
      },
    });
  };

  const getGeolocation = (params: {
    ip: GeolocationParams['ip'];
    geolocationViewType?: GEOLOCATION_VIEW_TYPE;
  }) => {
    if (isLogin && isLocationServiceConsent) {
      return AuthApi['get:/wello/v2/auth/wello-policy-code/geolocation']({
        query: {
          ip: params.ip,
          geolocationViewType:
            params.geolocationViewType ?? GEOLOCATION_VIEW_TYPE.WELLO_CLIENT,
        },
      });
    }

    return UnauthApi['get:/wello/v2/allows/wello-policy-code/geolocation']({
      query: {
        ip: params.ip,
      },
    });
  };

  return {
    getGeocoding,
    getGeolocation,
  };
};
